/**
 * Clipboard Panel Component
 *
 * Universal clipboard interface for viewing history, paste, and export.
 */
import { useState } from 'react';
import type { ClipboardEntry, PasteOptions } from '@amorphous/core';
import type { UseClipboardResult } from '../hooks/useClipboard';
import { FileUploader } from './FileUploader';
import { PdfPreview } from './PdfPreview';
import './ClipboardPanel.css';

interface ClipboardPanelProps {
  clipboard: UseClipboardResult;
  onClose?: () => void;
}

type ViewMode = 'history' | 'paste' | 'details';
type FilterType = 'all' | 'text' | 'media' | 'code' | 'aos';

export function ClipboardPanel({ clipboard, onClose }: ClipboardPanelProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('history');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [pasteText, setPasteText] = useState('');
  const [pasteLabel, setPasteLabel] = useState('');
  const [pasteTags, setPasteTags] = useState('');
  const [selectedEntry, setSelectedEntry] = useState<ClipboardEntry | null>(null);
  const [previewData, setPreviewData] = useState<string | null>(null);
  const [pdfData, setPdfData] = useState<Uint8Array | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const {
    entries,
    loading,
    error,
    stats,
    downloadProgress,
    pasteFromBrowser,
    paste,
    yank,
    copyToBrowser,
    download,
    deleteEntry,
    getTypeLabel,
    isSystemType,
    refresh,
  } = clipboard;

  // Filter entries by type
  const filteredEntries = entries.filter(entry => {
    if (filterType === 'all') return true;
    const type = entry.content.content.type;
    switch (filterType) {
      case 'text':
        return type.startsWith('text/') && !type.startsWith('text/x-');
      case 'media':
        return type.startsWith('image/') || type.startsWith('video/') || type.startsWith('audio/');
      case 'code':
        return type.startsWith('text/x-');
      case 'aos':
        return isSystemType(type);
      default:
        return true;
    }
  });

  // Handle paste from browser
  const handlePasteFromBrowser = async () => {
    const result = await pasteFromBrowser();
    if (result?.success) {
      setViewMode('history');
    }
  };

  // Handle manual paste
  const handleManualPaste = async () => {
    if (!pasteText.trim()) return;

    const options: PasteOptions = {};
    if (pasteLabel.trim()) options.label = pasteLabel.trim();
    if (pasteTags.trim()) {
      options.tags = pasteTags.split(',').map(t => t.trim()).filter(Boolean);
    }

    const result = await paste(pasteText, options);
    if (result.success) {
      setPasteText('');
      setPasteLabel('');
      setPasteTags('');
      setViewMode('history');
    }
  };

  // Handle entry click
  const handleEntryClick = async (entry: ClipboardEntry) => {
    setSelectedEntry(entry);
    setViewMode('details');
    setLoadingPreview(true);
    setPreviewData(null);
    setPdfData(null);

    try {
      const result = await yank(entry.hash);
      if (result.success && result.data) {
        const type = entry.content.content.type;
        if (type.startsWith('text/') || type === 'application/json') {
          setPreviewData(new TextDecoder().decode(result.data));
        } else if (type.startsWith('image/')) {
          // Create a clean ArrayBuffer to avoid SharedArrayBuffer issues
          const buffer = new ArrayBuffer(result.data.length);
          new Uint8Array(buffer).set(result.data);
          const blob = new Blob([buffer], { type });
          setPreviewData(URL.createObjectURL(blob));
        } else if (type === 'application/pdf') {
          // Store PDF data for PdfPreview component
          setPdfData(result.data);
        }
      }
    } catch {
      // Ignore preview errors
    } finally {
      setLoadingPreview(false);
    }
  };

  // Handle copy
  const handleCopy = async (entryHash: string) => {
    const success = await copyToBrowser(entryHash);
    if (success) {
      // Show brief feedback
    }
  };

  // Handle download
  const handleDownload = async (entryHash: string) => {
    await download(entryHash);
  };

  // Handle delete
  const handleDelete = async (entryHash: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!confirm('Delete this clipboard entry?')) return;

    const result = await deleteEntry(entryHash);
    if (!result.success) {
      alert(result.error || 'Failed to delete');
    } else if (selectedEntry?.hash === entryHash) {
      setViewMode('history');
    }
  };

  // Format timestamp
  const formatTime = (ts: number) => {
    const date = new Date(ts);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  // Format file size
  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Get type icon
  const getTypeIcon = (type: string) => {
    if (type.startsWith('image/')) return '🖼️';
    if (type.startsWith('video/')) return '🎬';
    if (type.startsWith('audio/')) return '🎵';
    if (type.startsWith('text/x-')) return '💻';
    if (type === 'application/aos-boot') return '🚀';
    if (type === 'application/aos-package') return '📦';
    if (type === 'application/aos-invite') return '✉️';
    if (type === 'application/json') return '{ }';
    if (type === 'application/pdf') return '📄';
    return '📋';
  };

  if (loading) {
    return (
      <div className="clipboard-panel">
        <div className="clipboard-loading">Loading clipboard...</div>
      </div>
    );
  }

  return (
    <div className="clipboard-panel">
      {/* Header */}
      <div className="clipboard-header">
        <h2>Universal Clipboard</h2>
        {onClose && (
          <button className="close-btn" onClick={onClose}>×</button>
        )}
      </div>

      {/* Tabs */}
      <div className="clipboard-tabs">
        <button
          className={`tab ${viewMode === 'history' ? 'active' : ''}`}
          onClick={() => setViewMode('history')}
        >
          History
        </button>
        <button
          className={`tab ${viewMode === 'paste' ? 'active' : ''}`}
          onClick={() => setViewMode('paste')}
        >
          Paste
        </button>
        {selectedEntry && (
          <button
            className={`tab ${viewMode === 'details' ? 'active' : ''}`}
            onClick={() => setViewMode('details')}
          >
            Details
          </button>
        )}
      </div>

      {/* Error display */}
      {error && (
        <div className="clipboard-error">
          {error.message}
        </div>
      )}

      {/* History View */}
      {viewMode === 'history' && (
        <div className="clipboard-history">
          {/* Filter */}
          <div className="clipboard-filter">
            <select
              value={filterType}
              onChange={e => setFilterType(e.target.value as FilterType)}
            >
              <option value="all">All Types</option>
              <option value="text">Text</option>
              <option value="media">Media</option>
              <option value="code">Code</option>
              <option value="aos">AOS Types</option>
            </select>
            <button className="refresh-btn" onClick={refresh}>
              ↻
            </button>
            <button className="paste-browser-btn" onClick={handlePasteFromBrowser}>
              📋 Paste from Browser
            </button>
          </div>

          {/* Stats */}
          {stats && (
            <div className="clipboard-stats">
              <span>{stats.totalPastes} items</span>
              <span>{formatSize(stats.totalBytes)} total</span>
            </div>
          )}

          {/* Entry list */}
          <div className="clipboard-list">
            {filteredEntries.length === 0 ? (
              <div className="clipboard-empty">
                No clipboard entries yet. Paste something to get started.
              </div>
            ) : (
              filteredEntries.map(entry => (
                <div
                  key={entry.hash}
                  className={`clipboard-entry ${entry === selectedEntry ? 'selected' : ''}`}
                  onClick={() => handleEntryClick(entry)}
                >
                  <div className="entry-icon">
                    {getTypeIcon(entry.content.content.type)}
                  </div>
                  <div className="entry-info">
                    <div className="entry-label">
                      {entry.content.meta.label || getTypeLabel(entry.content.content.type)}
                    </div>
                    <div className="entry-meta">
                      <span className="entry-time">{formatTime(entry.timestamp)}</span>
                      <span className="entry-size">{formatSize(entry.content.content.size)}</span>
                      <span className="entry-device">{entry.content.from.deviceAlias}</span>
                    </div>
                    {entry.content.meta.tags && entry.content.meta.tags.length > 0 && (
                      <div className="entry-tags">
                        {entry.content.meta.tags.map((tag: string) => (
                          <span key={tag} className="tag">{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="entry-actions">
                    <button
                      className="action-btn"
                      onClick={(e) => { e.stopPropagation(); handleCopy(entry.hash); }}
                      title="Copy to clipboard"
                    >
                      📋
                    </button>
                    <button
                      className="action-btn"
                      onClick={(e) => { e.stopPropagation(); handleDownload(entry.hash); }}
                      title="Download"
                    >
                      ⬇️
                    </button>
                    <button
                      className="action-btn delete-btn"
                      onClick={(e) => handleDelete(entry.hash, e)}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Paste View */}
      {viewMode === 'paste' && (
        <div className="clipboard-paste">
          <div className="paste-section">
            <h3>Upload Files</h3>
            <p>Drag and drop files or click to browse.</p>
            <FileUploader
              onUpload={async (data, options) => {
                const result = await paste(data, options);
                if (result.success) {
                  setViewMode('history');
                }
                return result;
              }}
            />
          </div>

          <div className="paste-divider">or</div>

          <div className="paste-section">
            <h3>Paste from Browser</h3>
            <p>Click the button below to paste content from your system clipboard.</p>
            <button className="paste-browser-btn large" onClick={handlePasteFromBrowser}>
              📋 Paste from Browser Clipboard
            </button>
          </div>

          <div className="paste-divider">or</div>

          <div className="paste-section">
            <h3>Paste Text Manually</h3>
            <textarea
              className="paste-textarea"
              value={pasteText}
              onChange={e => setPasteText(e.target.value)}
              placeholder="Paste or type content here..."
              rows={6}
            />
            <div className="paste-options">
              <input
                type="text"
                className="paste-input"
                value={pasteLabel}
                onChange={e => setPasteLabel(e.target.value)}
                placeholder="Label (optional)"
              />
              <input
                type="text"
                className="paste-input"
                value={pasteTags}
                onChange={e => setPasteTags(e.target.value)}
                placeholder="Tags, comma, separated (optional)"
              />
            </div>
            <button
              className="paste-submit-btn"
              onClick={handleManualPaste}
              disabled={!pasteText.trim()}
            >
              Save to Clipboard
            </button>
          </div>
        </div>
      )}

      {/* Details View */}
      {viewMode === 'details' && selectedEntry && (
        <div className="clipboard-details">
          <div className="details-header">
            <span className="details-icon">{getTypeIcon(selectedEntry.content.content.type)}</span>
            <div className="details-title">
              <h3>{selectedEntry.content.meta.label || getTypeLabel(selectedEntry.content.content.type)}</h3>
              <span className="details-type">{selectedEntry.content.content.type}</span>
            </div>
          </div>

          <div className="details-meta">
            <div className="meta-row">
              <span className="meta-label">Created</span>
              <span className="meta-value">{new Date(selectedEntry.timestamp).toLocaleString()}</span>
            </div>
            <div className="meta-row">
              <span className="meta-label">Size</span>
              <span className="meta-value">{formatSize(selectedEntry.content.content.size)}</span>
            </div>
            <div className="meta-row">
              <span className="meta-label">Device</span>
              <span className="meta-value">{selectedEntry.content.from.deviceAlias}</span>
            </div>
            <div className="meta-row">
              <span className="meta-label">Chunks</span>
              <span className="meta-value">{selectedEntry.content.content.chunks}</span>
            </div>
            <div className="meta-row">
              <span className="meta-label">Export</span>
              <span className="meta-value">{selectedEntry.content.permissions.export}</span>
            </div>
            <div className="meta-row">
              <span className="meta-label">View</span>
              <span className="meta-value">{selectedEntry.content.permissions.view}</span>
            </div>
            {selectedEntry.content.meta.filename && (
              <div className="meta-row">
                <span className="meta-label">Filename</span>
                <span className="meta-value">{selectedEntry.content.meta.filename}</span>
              </div>
            )}
            {selectedEntry.content.meta.tags && selectedEntry.content.meta.tags.length > 0 && (
              <div className="meta-row">
                <span className="meta-label">Tags</span>
                <span className="meta-value">
                  {selectedEntry.content.meta.tags.map((tag: string) => (
                    <span key={tag} className="tag">{tag}</span>
                  ))}
                </span>
              </div>
            )}
          </div>

          {/* Preview */}
          <div className="details-preview">
            <h4>Preview</h4>
            {downloadProgress && downloadProgress.entryHash === selectedEntry.hash ? (
              <div className="preview-downloading">
                <div className="download-status">
                  {downloadProgress.status === 'fetching' && 'Requesting content from peers...'}
                  {downloadProgress.status === 'downloading' && 'Downloading chunks...'}
                  {downloadProgress.status === 'complete' && 'Download complete!'}
                  {downloadProgress.status === 'error' && `Error: ${downloadProgress.error}`}
                </div>
                <div className="download-progress-bar">
                  <div
                    className={`download-progress-fill ${downloadProgress.status}`}
                    style={{ width: `${Math.round(downloadProgress.progress * 100)}%` }}
                  />
                </div>
                <div className="download-stats">
                  {downloadProgress.chunksReceived} / {downloadProgress.chunksTotal} chunks
                  ({Math.round(downloadProgress.progress * 100)}%)
                </div>
              </div>
            ) : loadingPreview ? (
              <div className="preview-loading">Loading preview...</div>
            ) : pdfData ? (
              <PdfPreview
                data={pdfData}
                filename={selectedEntry.content.meta.filename}
                thumbnailWidth={300}
              />
            ) : previewData ? (
              selectedEntry.content.content.type.startsWith('image/') ? (
                <img src={previewData} alt="Preview" className="preview-image" />
              ) : (
                <pre className="preview-text">{previewData.slice(0, 5000)}</pre>
              )
            ) : (
              <div className="preview-unavailable">Preview not available</div>
            )}
          </div>

          {/* Actions */}
          <div className="details-actions">
            <button onClick={() => handleCopy(selectedEntry.hash)}>
              📋 Copy to Browser
            </button>
            <button onClick={() => handleDownload(selectedEntry.hash)}>
              ⬇️ Download
            </button>
            <button className="delete-btn" onClick={() => handleDelete(selectedEntry.hash)}>
              🗑️ Delete
            </button>
            <button onClick={() => setViewMode('history')}>
              ← Back to History
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
